/* eslint-disable no-async-promise-executor */
import axios, { AxiosInstance, AxiosRequestConfig } from 'axios';
import { get } from 'lodash';
import crypto from 'crypto';

class QwaPublicAPIAccess {
  private axios: AxiosInstance;

  constructor() {
    this.axios = axios.create({
      baseURL: process.env.NEXT_PUBLIC_API_BASE_URL,
      withCredentials: true,
      headers: {
        'Content-type': 'application/json',
      },
    });

    this.axios.interceptors.request.use(async (config) => {
      if (!config.headers) {
        config.headers = new axios.AxiosHeaders();
      }

      const expiryTimestamp = Math.floor(Date.now() / 1000) + 10; // Valid for 10 seconds
      const secret = process.env.NEXT_PUBLIC_QWA_EXTERNAL_API_PASSWORD; // You would want to retrieve this from an environment variable or another secure source

      // Create HMAC hash of the expiry timestamp
      const hmac = secret ? crypto.createHmac('sha256', secret).update(expiryTimestamp.toString()).digest('hex') : '';

      config.headers['x-request-expiry'] = expiryTimestamp;
      config.headers['x-request-hmac'] = hmac;

      config.headers['Content-Type'] = 'application/json';
      return config;
    });

    this.axios.interceptors.response.use(undefined, async (error) => {
      if (
        error &&
        error.config &&
        error.response &&
        (error.response?.status === 401 || get(error, 'response.data.error.err.status') === 401)
      ) {
        return Promise.reject(error);
      }
      throw error;
    });
  }

  get = async <T = any>(url: string, config?: AxiosRequestConfig): Promise<T> => {
    try {
      const response = await this.axios.get<T>(url, config);
      return response.data;
    } catch (err) {
      console.error('REST API Service : get error', err);
      throw err;
    }
  };

  post = async <T = any, P = any>(url: string, payload: P): Promise<T> => {
    try {
      const response = await this.axios.post<T>(url, payload);
      return response.data;
    } catch (err) {
      console.error('REST API Service: post error', JSON.stringify(err, null, 2), err);
      throw err;
    }
  };

  delete = async <T = any, P = any>(url: string, payload: P): Promise<T> => {
    try {
      const response = await this.axios.delete(url, { data: payload });
      return response.data;
    } catch (err) {
      console.error('REST API Service : delete error', JSON.stringify(err, null, 2), err);
      throw err;
    }
  };

  put = async <T = any, P = any>(url: string, payload: P): Promise<T> => {
    try {
      const response = await this.axios.put<T>(url, payload);
      return response.data;
    } catch (err) {
      console.error('REST API Service : put error', JSON.stringify(err, null, 2), err);
      throw err;
    }
  };
}

export default new QwaPublicAPIAccess();
